import type * as React from 'react';
import { useMemo } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import type { Environment } from 'relay-runtime';
import { anonymousEnvironment, protectedEnvironment, publicEnvironment } from './environment';

/**
 * Determines which Relay environment to use based on the route pathname.
 *
 * - anonymousEnvironment: For fund detail routes accessed via sf/ or gf/ paths
 * - protectedEnvironment: For infoservice routes
 * - publicEnvironment: Default for all other routes (including direct ISIN paths like /AT...)
 *
 * Note: Direct ISIN paths (e.g. /AT0000A1E853) use publicEnvironment because the
 * /session/graphql endpoint returns VisitorInfoPage for these paths. Only sf/ and gf/
 * prefixed routes use the anonymous session endpoint.
 *
 * @param pathname - The current route pathname
 * @returns The appropriate Relay Environment
 */
export function getEnvironmentForPath(pathname: string): Environment {
  if (pathname.indexOf('sf/') > -1 || pathname.indexOf('gf/') > -1) {
    return anonymousEnvironment;
  }
  if (pathname.indexOf('infoservice') > -1) {
    return protectedEnvironment;
  }
  return publicEnvironment;
}

interface EnvironmentProviderWrapperProps {
  pathname: string;
  children: React.ReactNode;
}

/**
 * Wrapper component that provides the correct Relay environment based on the current route.
 * This enables all child components to access the appropriate environment via React context.
 */
export function EnvironmentProviderWrapper({ pathname, children }: EnvironmentProviderWrapperProps): React.ReactElement {
  const environment = useMemo(() => getEnvironmentForPath(pathname), [pathname]);

  return <RelayEnvironmentProvider environment={environment}>{children}</RelayEnvironmentProvider>;
}

export default EnvironmentProviderWrapper;
